import { AxiosError } from 'axios';
import { assign, createMachine } from 'xstate';

import { AddressSuggestion, Recipient } from 'api/api';

import { bootstrapConfig } from '~src/context/config';
import { apiInstance } from '~src/global-state';

import { MetaAddressFormFields } from '../types';

type MachineArgs = {
  initialEntry: boolean;
  hideAddressBook?: boolean;
  externalAddressBookProvider?: string;
  addressBookLength?: number;
};

type ServiceResponseError = AxiosError<{ message?: string }>;

export const createRecipientFlowMachine = ({
  initialEntry,
  hideAddressBook = false,
  externalAddressBookProvider,
  addressBookLength = 0,
}: MachineArgs) =>
  createMachine(
    {
      predictableActionArguments: true,
      id: 'formState',
      context: {
        hideAddressBook,
        addressBookLength,
        currentAddress: undefined,
        externalAddressBookProvider,
        errorMessage: undefined,
        categoryLost: false,
        locationLost: false,
        cleanForm: false,
        prevState: null,
        extendedValidation: {
          address: undefined,
          suggestion: undefined,
          isSubmissionBlocking: false,
        },
      },
      schema: {
        context: {} as Context,
        events: {} as RecipientFlowEvents,
        services: {} as Services,
      },
      tsTypes: {} as import('./recipient-flow-machine.typegen').Typegen0,
      type: 'parallel',
      states: {
        checkout: {
          initial: getInitialStateNode(
            initialEntry,
            addressBookLength,
            hideAddressBook,
            externalAddressBookProvider
          ),
          states: {
            addressSummary: {
              on: {
                BUTTON_CLICK: [
                  { cond: 'addressBookHidden', target: 'inlineAddressForm' },
                  { cond: 'editInitialAddress', target: 'inlineAddressForm' },
                  { target: '#formState.modal.modalAddressList' },
                ],
              },
            },
            inlineAddressForm: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateAddressService',
                    onDone: {
                      target: '#formState.checkout.addressSummary',
                      actions: 'updateAddressBookLength',
                    },
                    onError: {
                      target: ['idle', '#formState.modal.errorModal'],
                      actions: assign((_, e) => ({
                        errorMessage: (e.data as ServiceResponseError).response?.data.message,
                        prevState: 'inlineAddressForm',
                      })),
                    },
                  },
                },
              },
              on: { SUBMIT: { target: '.submitting', actions: 'updateCurrentAddress' } },
            },
            inlineAddressList: {
              on: {
                OPEN_ADDRESS: {
                  target: '#formState.modal.modalAddressForm',
                  actions: 'updateCurrentAddress',
                },
                SUBMIT: { target: '#formState.modal.modalAddressForm' },
                ADD_NEW_ADDRESS: {
                  target: '#formState.modal.modalAddressForm',
                  actions: 'updateCurrentAddress',
                },
              },
            },
          },
        },
        modal: {
          initial: 'hidden',
          states: {
            hidden: {},
            modalAddressList: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateAddressService',
                    onDone: {
                      target: ['#formState.modal.hidden', '#formState.checkout.addressSummary'],
                      actions: 'updateAddressBookLength',
                    },
                  },
                },
              },
              on: {
                ADD_NEW_ADDRESS: {
                  target: '#formState.modal.modalAddressForm',
                  actions: 'updateCurrentAddress',
                },
                OPEN_ADDRESS: {
                  target: '#formState.modal.modalAddressForm',
                  actions: 'updateCurrentAddress',
                },
                CLOSE: { target: ['hidden', '#formState.checkout.addressSummary'] },
                SUBMIT: [
                  {
                    cond: 'isAddressDirty',
                    target: 'modalAddressForm',
                    actions: 'updateCurrentAddress',
                  },
                  {
                    cond: 'isRecipientTheSame',
                    target: ['#formState.modal.hidden', '#formState.checkout.addressSummary'],
                  }, // If the recipient is the same as chosen address - skip submitting
                  { target: '.submitting', actions: 'updateCurrentAddress' },
                ],
              },
            },
            modalAddressForm: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateAddressService',
                    onDone: {
                      target: ['#formState.modal.hidden', '#formState.checkout.addressSummary'],
                      actions: ['updateAddressBookLength', 'resetCurrentAddress'],
                    },
                    onError: {
                      target: '#formState.modal.errorModal',
                      actions: assign((_, e) => ({
                        errorMessage: (e.data as ServiceResponseError).response?.data.message,
                        prevState: 'modalAddressForm',
                      })),
                    },
                  },
                },
                updateRecipientAfterDelete: {
                  tags: 'fetching',
                  invoke: {
                    src: 'updateAddressService',
                    onDone: {
                      target: '#formState.modal.modalAddressList',
                      actions: 'updateAddressBookLength',
                    },
                    onError: {
                      target: '#formState.modal.errorModal',
                      actions: assign((_, e) => ({
                        errorMessage: (e.data as ServiceResponseError).response?.data.message,
                        prevState: 'addressSummary',
                      })), // automatically submitted by us - go to address summary,
                    },
                  },
                },
                deleteAddress: {
                  always: [
                    {
                      cond: 'addressBookLongerThanOne',
                      target: 'deletingAddress',
                    },
                    {
                      target: 'idle',
                      actions: assign({ cleanForm: true }),
                    },
                  ],
                },
                deletingAddress: {
                  tags: 'fetching',
                  invoke: {
                    src: 'deleteAddressService',
                    onDone: { target: 'updateRecipientAfterDelete' },
                    onError: {
                      target: '#formState.modal.errorModal',
                      actions: assign((ctx, e) => ({
                        ...ctx,
                        errorMessage: (e.data as ServiceResponseError).response?.data.message,
                        prevState: 'modalAddressFrom',
                      })),
                    },
                  },
                },
              },
              on: {
                SUBMIT: [
                  {
                    target: '.submitting',
                    actions: [assign({ cleanForm: false })],
                  },
                ],
                CLOSE: [
                  { cond: 'isExternalProvider', target: 'hidden' }, // TODO: probably we should handle modalAddressList and inlineAddressList differently
                  { cond: 'isAddressDirty', target: '#formState.checkout.inlineAddressList' },
                  {
                    target: '#formState.modal.modalAddressList',
                    actions: assign({ cleanForm: false }),
                  },
                ],
                DELETE: {
                  cond: 'isNotExternalProvider',
                  target: '.deleteAddress',
                  actions: 'updateCurrentAddress',
                },
              },
            },
            extendedValidationModal: {
              initial: 'idle',
              states: {
                idle: {},
                submitting: {
                  exit: 'clearExtendedValidationAddresses',
                  tags: 'fetching',
                  invoke: {
                    src: 'updateAddressService',
                    onDone: {
                      target: ['#formState.modal.hidden', '#formState.checkout.addressSummary'],
                      actions: ['updateAddressBookLength', 'resetCurrentAddress'],
                    },
                    onError: {
                      target: '#formState.modal.errorModal',
                      actions: assign((_, e) => ({
                        errorMessage: (e.data as ServiceResponseError).response?.data.message,
                        prevState: 'extendedValidationModal', //TODO change this
                      })),
                    },
                  },
                },
              },
              on: {
                CLOSE: [
                  {
                    cond: 'isInitialEntry',
                    target: 'hidden',
                  },
                  {
                    target: '#formState.modal.modalAddressForm',
                  },
                ],
                // HACK: naming the event SUBMIT causes double transition to .submitting state in inline mode becuase of the parallel machines
                SUBMIT_ADDRESS: { target: '.submitting' },
              },
            },
            errorModal: {
              initial: 'entry',
              states: {
                entry: {
                  always: [
                    { cond: 'isLocationLost', target: 'locationLost' },
                    { cond: 'isCategoryLost', target: 'categoryLost' },
                    { cond: 'isInvalidPostalCode', target: 'invalidPostalCode' },
                    { cond: 'noShippingOptions', target: 'noShippingOptions' },
                    { cond: 'noOptionsForAddress', target: 'noOptionsForAddress' },
                    { target: 'generalError' },
                  ],
                },
                locationLost: {},
                categoryLost: {},
                invalidPostalCode: {},
                generalError: {},
                noShippingOptions: {},
                noOptionsForAddress: {},
              },
              on: {
                CLOSE: [
                  {
                    cond: ctx => ctx.prevState === 'modalAddressForm' && ctx.locationLost,
                    target: ['hidden', '#formState.checkout.addressSummary'], // for now hide the modal
                    actions: ['resetPrevErrorState', 'resetLocationLost'],
                  },
                  {
                    cond: ctx => ctx.prevState === 'addressSummary' && ctx.locationLost,
                    target: ['hidden', '#formState.checkout.addressSummary'],
                    actions: ['resetPrevErrorState', 'resetLocationLost'],
                  },
                  {
                    cond: ctx => ctx.prevState === 'inlineAddressForm' && ctx.locationLost,
                    target: ['hidden', '#formState.checkout.inlineAddressForm'],
                    actions: ['resetPrevErrorState', 'resetLocationLost'],
                  },
                  {
                    cond: ctx => ctx.prevState === 'modalAddressForm' && ctx.categoryLost,
                    target: ['hidden', '#formState.checkout.addressSummary'], // for now hide the modal
                    actions: ['resetPrevErrorState', 'resetCategoryLost'],
                  },
                  {
                    cond: ctx => ctx.prevState === 'addressSummary' && ctx.categoryLost,
                    target: ['hidden', '#formState.checkout.addressSummary'],
                    actions: ['resetPrevErrorState', 'resetCategoryLost'],
                  },
                  {
                    cond: ctx => ctx.prevState === 'inlineAddressForm' && ctx.categoryLost,
                    target: ['hidden', '#formState.checkout.inlineAddressForm'],
                    actions: ['resetPrevErrorState', 'resetCategoryLost'],
                  },
                  {
                    cond: ctx => ctx.prevState === 'modalAddressForm',
                    target: '#formState.modal.modalAddressForm',
                    actions: 'resetPrevErrorState',
                  },
                  {
                    cond: ctx => ctx.prevState === 'addressSummary',
                    target: ['hidden', '#formState.checkout.addressSummary'],
                    actions: 'resetPrevErrorState',
                  },
                  {
                    cond: ctx => ctx.prevState === 'inlineAddressForm',
                    target: ['hidden', '#formState.checkout.inlineAddressForm'],
                    actions: 'resetPrevErrorState',
                  },
                  {
                    target: [
                      'hidden',
                      `#formState.checkout.${getInitialStateNode(
                        initialEntry,
                        addressBookLength,
                        hideAddressBook,
                        externalAddressBookProvider
                      )}`,
                    ],
                  },
                ],
              },
            },
          },
        },
      },
      on: {
        UPDATE_COUNT: { actions: 'updateAddressBookLength' },
        SHOW_EXTENDED_VALIDATION_MODAL: {
          target: '#formState.modal.extendedValidationModal',
          actions: [
            'updateCurrentAddress',
            assign((_, e) => ({
              extendedValidation: e.data,
            })),
          ],
        },
      },
    },
    {
      services: {
        deleteAddressService: async ctx => {
          const headers = ctx.externalAddressBookProvider
            ? { headers: { ['x-ingrid-checkout-session-id']: bootstrapConfig.sessionId } }
            : undefined;

          const { data } = await apiInstance.v4.deleteAddressFromAddressBook(
            ctx.currentAddress!.id!,
            headers
          );
          return { address: data };
        },
      },
      actions: {
        clearExtendedValidationAddresses: assign({
          extendedValidation: {
            address: undefined,
            suggestion: undefined,
            isSubmissionBlocking: false,
          },
        }),
        updateAddressBookLength: assign((ctx, e) => ({
          ...ctx,
          addressBookLength: e.data.addressBookLength,
        })),
        updateCurrentAddress: assign((_, e) => ({ currentAddress: e.data.address })),
        resetCurrentAddress: assign({ currentAddress: undefined }), //TODO add prefill case
        resetPrevErrorState: assign({ prevState: null, errorMessage: undefined }),
      },
      guards: {
        isLocationLost: ctx => !!ctx.locationLost,
        isCategoryLost: ctx => !!ctx.categoryLost,
        isInvalidPostalCode: ctx => ctx.errorMessage === 'invalid postal code',
        noShippingOptions: ctx => ctx.errorMessage === 'no shipping options',
        addressBookHidden: ctx => !!ctx.hideAddressBook,
        isExternalProvider: ctx => !!ctx.externalAddressBookProvider,
        isNotExternalProvider: ctx => !ctx.externalAddressBookProvider,
        isAddressDirty: ctx => !!ctx.currentAddress?.is_dirty,
        addressBookLongerThanOne: ctx => ctx.addressBookLength > 1,
      },
    }
  );

const getInitialStateNode = (
  initial: boolean,
  addressBookLength: number,
  hideAddressBook: boolean,
  externalAddressBook?: string
) => {
  if (!!externalAddressBook) {
    if (!initial) {
      return 'addressSummary';
    } else if (!hideAddressBook && addressBookLength > 1) {
      return 'inlineAddressList';
    } else {
      return 'inlineAddressForm';
    }
  } else {
    if (initial) {
      return 'inlineAddressForm';
    } else {
      return 'addressSummary';
    }
  }
};

export type Context = {
  hideAddressBook: MachineArgs['hideAddressBook'];
  externalAddressBookProvider: MachineArgs['externalAddressBookProvider'];
  addressBookLength: number;
  currentAddress?: Recipient;
  errorMessage?: string;
  categoryLost: boolean;
  locationLost: boolean;
  cleanForm: boolean;
  prevState: string | null; // TODO: type this correctly
  extendedValidation: {
    address?: MetaAddressFormFields;
    suggestion?: AddressSuggestion;
    isSubmissionBlocking: boolean;
  };
};

export type RecipientFlowEvents =
  | { type: 'BUTTON_CLICK' }
  | { type: 'OPEN_ADDRESS'; data: { address: Recipient } }
  | { type: 'CLOSE'; data?: { shouldResetForm: boolean } }
  | { type: 'DELETE'; data: { address: Recipient } }
  | { type: 'SUBMIT'; data: { address: Recipient; onError?: () => void } }
  | { type: 'ADD_NEW_ADDRESS'; data: { address: Recipient } }
  | { type: 'CONFIRM' }
  | { type: 'UPDATE_COUNT'; data: { addressBookLength: number } }
  | { type: 'LOCATION_LOST' }
  | { type: 'SUBMIT_ADDRESS'; data: { address: Recipient } }
  | {
      type: 'SHOW_EXTENDED_VALIDATION_MODAL';
      data: {
        address: MetaAddressFormFields;
        suggestion: AddressSuggestion;
        isSubmissionBlocking: boolean;
      };
    };

type Services = {
  updateAddressService: {
    data: { categoryLost?: boolean; locationLost?: boolean; addressBookLength: number };
  };
  deleteAddressService: { data: { address: Recipient } };
};

export type RecipientFlowMachine = ReturnType<typeof createRecipientFlowMachine>;
